import React, { useState } from 'react';
import { useAppStoreHome } from '../context/AppStoreHomeContext';
import { sendGuideMessage } from '../api/guideChatAPI';
import './ChatSidePanel.css';

const QUICK_LINKS = [
  { label: 'Explore', path: '/apps-store/marketplace', desc: 'Browse apps' },
  { label: 'Interview Simulator', path: '/website-interview', desc: 'Practice video interviews' },
  { label: 'Quiz Generator', path: '/quiz-generation', desc: 'Practice questions with solutions' },
  { label: 'Application Builder', path: '/application-builder', desc: 'CV, cover letter, personal statement' },
  { label: 'Case Studies', path: '/case-studies', desc: 'Browse and complete cases' },
  { label: 'My Path', path: '/apps-store', desc: 'Your timeline and tasks' },
];

function ChatSidePanel({ isOpen, onClose, onNavigate, apps = [] }) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastError, setLastError] = useState('');
  const [lastReply, setLastReply] = useState(null);
  const { tasks, setTasks, addTask, addBubble } = useAppStoreHome();

  const handleQuickLink = (path) => {
    if (onNavigate) onNavigate(path);
    onClose?.();
  };

  const handleSubmit = async () => {
    const text = (input || '').trim();
    if (!text) return;
    setInput('');
    setLoading(true);
    setLastError('');
    setLastReply(null);
    try {
      const result = await sendGuideMessage(text, tasks, apps);
      const reply = result.reply || 'Use Quick access below to go to an app.';
      const options = Array.isArray(result.options) && result.options.length > 0
        ? result.options
        : result.navigate
          ? [{ label: reply, link: result.navigate }]
          : [];

      if (result.suggest_add_task && result.suggest_add_task.label) {
        addTask(result.suggest_add_task.label, result.suggest_add_task.link || null);
      }
      if (result.suggest_rewrite_tasks && Array.isArray(result.suggest_rewrite_tasks) && result.suggest_rewrite_tasks.length > 0) {
        setTasks(result.suggest_rewrite_tasks.slice(0, 3).map((t, i) => ({
          id: 't' + Date.now() + i,
          label: t.label || 'Task',
          completed: false,
          link: t.link || null,
        })));
      }
      setLastReply({ reply, options, userQuery: text });
    } catch (e) {
      setLastError('Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleOptionClick = (option) => {
    addBubble({
      type: 'chat_suggestion',
      title: option.label,
      subtitle: 'Click to open',
      link: option.link || null,
    });
    if (option.link && onNavigate) onNavigate(option.link);
    onClose?.();
    setLastReply(null);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="chat-side-panel-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="chat-side-panel" role="dialog" aria-label="Guide panel">
        <div className="chat-side-panel-header">
          <h2 className="chat-side-panel-title">Guide</h2>
          <button
            type="button"
            className="chat-side-panel-close close-x"
            onClick={onClose}
            aria-label="Close panel"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l18 18" />
            </svg>
          </button>
        </div>
        <p className="chat-side-panel-subtitle">
          Get directed to the right tools, apps, or experiences. I only guide you to apps or tasks.
        </p>
        {lastReply && (
          <div className="chat-side-panel-reply">
            <p className="chat-side-panel-reply-message">{lastReply.reply}</p>
            {lastReply.options.length > 0 && (
              <ul className="chat-side-panel-options-list">
                {lastReply.options.map((opt, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      className="chat-side-panel-option-item"
                      onClick={() => handleOptionClick(opt)}
                    >
                      {opt.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="chat-side-panel-quick">
          <h3 className="chat-side-panel-quick-title">Quick access</h3>
          <ul className="chat-side-panel-quick-list">
            {QUICK_LINKS.map((item) => (
              <li key={item.path}>
                <button
                  type="button"
                  className="chat-side-panel-quick-item"
                  onClick={() => handleQuickLink(item.path)}
                >
                  <span className="chat-side-panel-quick-label">{item.label}</span>
                  <span className="chat-side-panel-quick-desc">{item.desc}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="chat-side-panel-input-wrap">
          <div className="chat-side-panel-input-row">
            <input
              type="text"
              className="chat-side-panel-input"
              placeholder="Ask where to go or add a task..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
              aria-label="Search or ask"
              disabled={loading}
            />
            <button type="button" className="chat-side-panel-send" onClick={handleSubmit} disabled={loading}>
              {loading ? '...' : 'Send'}
            </button>
          </div>
          {lastError && <p className="chat-side-panel-error">{lastError}</p>}
        </div>
      </aside>
    </>
  );
}

export default ChatSidePanel;
